<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    padding: {
        type: String,
        default: ''
    },
    displayArea: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const deliveryData = ref(null);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const deliveryId = ref('');
const displayValue = ref('');
const selectedArea = ref('');

const findMatchedOption = (options, value) => {
    return options.find((option) => option.value === value && option.area === selectedArea.value) || options.find((option) => option.value === value);
};

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);

const filterOptions = () => {
    filteredOptions.value = deliveryData.value.filter((option) => option.area.toString().toLowerCase().includes(searchQuery.value.toLowerCase().trim()));
};

onMounted(async () => {
    await Promise.all([searchDelivery()]);
    nextTick(() => {
        filteredOptions.value = deliveryData.value;
    });
});

const searchDelivery = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/delivery', config, {
            immediate: false,
            onSuccess(response) {
                const data = response.data.data || [];

                const newList = data.flatMap((item) => {
                    if (!item.area_list || item.area_list.length === 0) {
                        return [];
                    }
                    return item.area_list.map((area) => ({
                        value: item.delivery_id,
                        text: item.delivery_name,
                        area: `${area.delivery_city} ${area.delivery_county} ${area.delivery_district}`,
                        cost: item.delivery_cost
                    }));
                });
                deliveryData.value = newList || [];
            },
            onError(err) {
                console.error('Error:', err.message);
                deliveryData.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        deliveryData.value = [];
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (deliveryData.value && deliveryData.value.length > 0) {
            const matchedOption = findMatchedOption(deliveryData.value, newVal);
            if (matchedOption) {
                displayValue.value = props.displayArea ? matchedOption.area : matchedOption.text;
                inputValue.value = matchedOption.value;
            } else {
                displayValue.value = '';
                inputValue.value = newVal;
            }
        } else {
            displayValue.value = '';
            inputValue.value = newVal;
        }
    },
    { immediate: true }
);

watch(
    deliveryData,
    (newdeliveryData) => {
        if (!newdeliveryData || newdeliveryData.length === 0) return;

        const matchedOption = findMatchedOption(newdeliveryData, props.modelValue);
        if (matchedOption) {
            displayValue.value = props.displayArea ? matchedOption.area : matchedOption.text;
            inputValue.value = matchedOption.value;
        }
    },
    { immediate: true, deep: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (props.upper) {
        value = value.toUpperCase();
    }

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    // 커서 위치 조정
    // if (lengthDiff !== 0) {
    //     cursorPosition = cursorPosition + lengthDiff;
    // }

    // 커서가 값의 길이를 초과하지 않도록 보장
    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value,
        area: '',
        cost: ''
    });
};

// 팝업 열기/닫기
const togglePopup = () => {
    if (!isReadonly) return;
    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

// 값 선택
const selectValue = (option) => {
    inputValue.value = option.value;
    selectedArea.value = option.area;
    displayValue.value = props.displayArea ? option.area : option.text;
    //emitUpdate(option.text);

    emit('update:modelValue', option.value);
    emit('input', {
        id: props.inputId,
        value: option.value,
        area: (props.displayArea ? option.area : option.text) || '',
        cost: option.cost || ''
    });

    deliveryId.value = option.value;
    ValidationMsg.value = '';

    closePopup();
};

const clearInput = () => {
    displayValue.value = '';
    deliveryId.value = '';
    selectedArea.value = '';
    emitUpdate('');
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filterOptions();
    }
});

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return '0';
    }
    return Number(value).toLocaleString();
};

const groupCounts = computed(() => {
    const counts = {};
    filteredOptions.value.forEach((opt) => {
        counts[opt.value] = (counts[opt.value] || 0) + 1;
    });
    return counts;
});

const displayOptions = computed(() => {
    const seen = {};
    return filteredOptions.value.map((opt) => {
        const count = groupCounts.value[opt.value] || 1;
        const isFirst = !seen[opt.value];
        seen[opt.value] = true;
        return {
            ...opt,
            isFirst,
            rowspan: count
        };
    });
});

defineExpose({
    deliveryId
});
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12">
            <input
                v-if="visible"
                type="text"
                :id="inputId"
                v-model="displayValue"
                class="p-inputtext cursor-pointer block w-full h-[2.5rem] ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'pr-24': button,
                    'error-border': ValidationMsg
                }"
                :placeholder="placeholder"
                :required="required"
                :readonly="isReadonly"
                :title="inputValue"
                @input="onInput"
                @click="togglePopup"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button mr-[calc(1rem)]">&times;</button>

                <!-- <button v-if="!isReadonly" @click="togglePopup" class="button-style">검색</button> -->
            </div>
        </div>
        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay">
                <div class="popup-content">
                    <!-- <h3>선택</h3> -->
                    <input v-model="searchQuery" @input="filterOptions" placeholder="행정구역을 검색하세요" class="search-input" style="margin-top: 1rem" />
                    <div class="options-container">
                        <table class="delivery-grid">
                            <thead>
                                <tr>
                                    <th style="display: none">지점id</th>
                                    <th style="width: 8rem">딜리버리명</th>
                                    <th>행정구역</th>
                                    <th>요금</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(option, idx) in displayOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                    <!-- 그룹 첫 행에만 hidden id cell  rowspan -->
                                    <td v-if="option.isFirst" style="display: none" :rowspan="option.rowspan">{{ option.value }}</td>

                                    <td v-if="option.isFirst" :rowspan="option.rowspan">{{ option.text }}</td>

                                    <!-- 항상 렌더링 되는 area 셀 -->
                                    <td>{{ option.area }}</td>

                                    <td v-if="option.isFirst" :rowspan="option.rowspan">{{ formatCurrency(option.cost) }} 원</td>
                                </tr>

                                <!-- <tr v-for="option in filteredOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                    <td style="display: none">{{ option.value }}</td>
                                    <td>{{ option.text }}</td>
                                    <td>{{ option.area }}</td>
                                    <td style="text-align: right; padding-right: 1rem">{{ formatCurrency(option.cost) }} 원</td>
                                </tr> -->
                            </tbody>
                        </table>
                    </div>
                    <button @click="closePopup" class="close-button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

input[readonly] {
    background-color: whitesmoke !important;
    cursor: default;
}

input[readonly]:focus {
    border-color: #d1d5db; /* 포커스 시 기본 테두리 색상 유지 */
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.caution-customer {
    color: #ff0000;
    font-weight: bold;
}

.error-border {
    border: 1px solid red !important;
}

.readonly-Bg {
    background-color: whitesmoke !important;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}
.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 36rem;
    text-align: left;
    min-width: 30rem;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 18rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.delivery-grid {
    width: 100%;
    border-collapse: collapse;
}

.delivery-grid th,
.delivery-grid td {
    border: 0.1rem solid #ddd;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
}

.delivery-grid th {
    background-color: #f2f2f2;
    position: sticky;
    top: 0;
    z-index: 10;
    text-align: center;
}

.delivery-grid td {
    text-align: left;
    padding: 0.3rem 1rem;
}

.delivery-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.delivery-grid th:hover {
    cursor: default;
}

.delivery-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
